import React, { useState, useEffect, useCallback } from 'react';
import { offlineStorage } from '../services/offlineStorage';
import { Archive, RefreshCw, Trash2, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

function formatDate(isoString) {
  if (!isoString) return '–';
  const d = new Date(isoString);
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function summarizeState(data) {
  if (!data) return { rooms: 0, totalItems: 0, totalVolume: 0, customerName: '', address: '' };
  const roomsData = data.roomsData || {};
  const rooms = Object.keys(roomsData).length;
  let totalItems = 0;
  let totalVolume = 0;
  Object.values(roomsData).forEach(room => {
    (room.items || []).forEach(item => {
      if (item.quantity > 0) {
        totalItems += item.quantity;
        totalVolume += ((item.length || 0) * (item.width || 0) * (item.height || 0) * item.quantity) / 1_000_000;
      }
    });
  });
  const moveInfo = data.moveInfo || {};
  const customerName = moveInfo.customerName || moveInfo.name || '';
  const address = moveInfo.fromAddress || moveInfo.address || '';
  return { rooms, totalItems, totalVolume: totalVolume.toFixed(2), customerName, address };
}

export default function CachedDealsTab({ onOpenDeal }) {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const all = await offlineStorage.getAllInspectionStates();
    // Sort newest first
    all.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setStates(all);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (dealId) => {
    if (!window.confirm(`Cache für Deal "${dealId}" wirklich löschen?`)) return;
    await offlineStorage.deleteInspectionState(dealId);
    setStates(prev => prev.filter(s => s.dealId !== dealId));
  };

  const toggleExpand = (dealId) => {
    setExpanded(prev => ({ ...prev, [dealId]: !prev[dealId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        Lade gecachte Besichtigungen…
      </div>
    );
  }

  if (states.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Archive className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="font-medium">Keine gecachten Besichtigungen vorhanden</p>
        <p className="text-sm mt-1">Daten werden automatisch während einer aktiven Besichtigung gespeichert.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{states.length} gecachte Besichtigung{states.length !== 1 ? 'en' : ''} im lokalen Speicher</p>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </div>

      {states.map(state => {
        const { rooms, totalItems, totalVolume, customerName, address } = summarizeState(state.data);
        const isExpanded = expanded[state.dealId];
        const roomsData = state.data?.roomsData || {};

        return (
          <div key={state.dealId} className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex items-start justify-between gap-3">
              <button
                className="flex items-center gap-2 text-left flex-1 min-w-0"
                onClick={() => toggleExpand(state.dealId)}
              >
                {isExpanded
                  ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                  : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 font-mono text-sm">Deal #{state.dealId}</span>
                    {customerName && (
                      <span className="text-gray-600 text-sm">– {customerName}</span>
                    )}
                  </div>
                  {address && <p className="text-xs text-gray-400 mt-0.5 truncate">{address}</p>}
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right text-xs text-gray-400 hidden sm:block">
                  <div>{formatDate(state.timestamp)}</div>
                  <div>{rooms} Räume · {totalItems} Gegenstände · {totalVolume} m³</div>
                </div>
                {onOpenDeal && (
                  <button
                    onClick={() => onOpenDeal(state.dealId)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Öffnen
                  </button>
                )}
                <button
                  onClick={() => handleDelete(state.dealId)}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded"
                  title="Cache löschen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="px-4 py-3 space-y-3">
                <div className="sm:hidden text-xs text-gray-400 mb-2">
                  Gespeichert: {formatDate(state.timestamp)} · {rooms} Räume · {totalItems} Gegenstände · {totalVolume} m³
                </div>
                {Object.entries(roomsData).map(([roomName, roomData]) => {
                  const filledItems = (roomData.items || []).filter(i => i.quantity > 0);
                  if (filledItems.length === 0) return null;
                  return (
                    <div key={roomName}>
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{roomName}</p>
                      <div className="space-y-1">
                        {filledItems.map((item, idx) => {
                          const vol = ((item.length || 0) * (item.width || 0) * (item.height || 0) * item.quantity) / 1_000_000;
                          const services = [
                            item.demontiert && 'Demontage',
                            item.remontiert && 'Remontage',
                            item.duebelarbeiten && 'Dübelarbeiten',
                            item.elektro && 'Elektro',
                          ].filter(Boolean);
                          return (
                            <div key={idx} className="flex items-baseline justify-between text-sm text-gray-700 border-b border-gray-100 pb-1 last:border-0">
                              <span>
                                {item.quantity}× {item.name}
                                {services.length > 0 && (
                                  <span className="ml-2 text-xs text-blue-600">[{services.join(', ')}]</span>
                                )}
                              </span>
                              <span className="text-xs text-gray-400 ml-4 shrink-0">{vol.toFixed(2)} m³</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {Object.values(roomsData).every(r => (r.items || []).every(i => !i.quantity)) && (
                  <p className="text-sm text-gray-400 italic">Keine Gegenstände erfasst.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
